import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

import { AppShell } from './components/Layout/AppShell';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Brand } from './components/ui';

import { Login } from './components/Auth/Login';
import { SignUp } from './components/Public/SignUp';
import { Dashboard } from './components/Dashboard/Dashboard';
import { UserList } from './components/UserManagement/UserList';
import { LicenseList } from './components/LicenseManagement/LicenseList';
import { DeviceList } from './components/DeviceManagement/DeviceList';
import { PropList } from './components/PropManagement/PropList';
import { RoleManagement } from './components/RoleManagement/RoleManagement';
import { SignupSettings } from './components/SignupSettings/SignupSettings';
import { PaymentSettings } from './components/Payments/PaymentSettings';
import { BotProcessSettings } from './components/Payments/BotProcessSettings';
import { BotUsers } from './components/Payments/BotUsers';
import { BotManualJobs } from './components/Payments/BotManualJobs';
import { ApiKeys } from './components/Payments/ApiKeys';
import { KeyboxManagement } from './components/KeyboxManagement/KeyboxManagement';
import LicenseRequestPage from './pages/LicenseRequestPage';

const ADMIN = 'Admin';
const USER_MANAGER = 'UserManager';

/** Branded splash so the session check does not flash a bare spinner. */
const SessionSplash: React.FC = () => (
  <Box
    sx={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      bgcolor: 'background.default',
    }}
  >
    <Brand size={44} />
    <CircularProgress size={22} thickness={5} />
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      Restoring session…
    </Typography>
  </Box>
);

/** Where a signed-in user belongs when they hit `/` or an unauthorised route. */
const landingFor = (groups: string[]) => {
  if (groups.includes(ADMIN)) return '/dashboard';
  if (groups.includes(USER_MANAGER)) return '/users';
  return '/login';
};

const ProtectedRoute: React.FC<{ allowedGroups: string[]; children: React.ReactNode }> = ({
  allowedGroups,
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) return <SessionSplash />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedGroups.some((group) => user.groups.includes(group))) {
    return <Navigate to={landingFor(user.groups)} replace />;
  }
  return <AppShell>{children}</AppShell>;
};

const RoleBasedRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return <SessionSplash />;
  return <Navigate to={user ? landingFor(user.groups) : '/login'} replace />;
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/signup" element={<SignUp />} />

    <Route
      path="/dashboard"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <Dashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/signup-settings"
      element={
        <ProtectedRoute allowedGroups={[ADMIN, USER_MANAGER]}>
          <SignupSettings />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payments"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <PaymentSettings />
        </ProtectedRoute>
      }
    />
    <Route
      path="/bot-process"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <BotProcessSettings />
        </ProtectedRoute>
      }
    />
    <Route
      path="/bot-users"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <BotUsers />
        </ProtectedRoute>
      }
    />
    <Route
      path="/bot-jobs"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <BotManualJobs />
        </ProtectedRoute>
      }
    />
    <Route
      path="/api-keys"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <ApiKeys />
        </ProtectedRoute>
      }
    />
    <Route
      path="/users"
      element={
        <ProtectedRoute allowedGroups={[ADMIN, USER_MANAGER]}>
          <UserList />
        </ProtectedRoute>
      }
    />
    <Route path="/licenses" element={<Navigate to="/licenses/michanger" replace />} />
    <Route
      path="/licenses/onechanger"
      element={
        <ProtectedRoute allowedGroups={[ADMIN, USER_MANAGER]}>
          <LicenseList />
        </ProtectedRoute>
      }
    />
    <Route
      path="/licenses/michanger"
      element={
        <ProtectedRoute allowedGroups={[ADMIN, USER_MANAGER]}>
          <LicenseList />
        </ProtectedRoute>
      }
    />
    <Route
      path="/license-requests"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <LicenseRequestPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/devices"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <DeviceList />
        </ProtectedRoute>
      }
    />
    <Route
      path="/props"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <PropList />
        </ProtectedRoute>
      }
    />
    <Route
      path="/keyboxes"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <KeyboxManagement />
        </ProtectedRoute>
      }
    />
    <Route
      path="/roles"
      element={
        <ProtectedRoute allowedGroups={[ADMIN]}>
          <RoleManagement />
        </ProtectedRoute>
      }
    />

    <Route path="/" element={<RoleBasedRedirect />} />
    <Route path="*" element={<RoleBasedRedirect />} />
  </Routes>
);

const App: React.FC = () => (
  <Router>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </Router>
);

export default App;
